import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  activeMultipleEntryPassesDetailsSelector,
  fetchMultipleEntryPasses,
  hasActiveMultipleEntryPassSelector,
  inactiveMultipleEntryPassesSelector,
  selectPassesFetched,
} from "../../store/multipleEntryPassSlice";

// The four pass states derivePassState can return. HIDDEN covers the
// never-fetched window so the promo banner doesn't flash.
export const PASS_STATE = Object.freeze({
  HIDDEN: "hidden",
  ACTIVE: "active",
  INACTIVE: "inactive",
  NONE: "none",
});

/**
 * Multi-entry-pass state, derived from the store alone — the single source of
 * truth for every V2 screen that renders the pass (profile, home).
 * Purchase eligibility (canPurchaseNewPass) is not part of this state — each
 * screen's data layer gates the promo on it separately (HomePassBanner,
 * useProfileData).
 *
 * @returns {{state: string, pass?: Object, endDate?: string}} state is a PASS_STATE value
 */
export const derivePassState = ({ loaded, hasActive, activePass, inactivePasses }) => {
  if (!loaded) return { state: PASS_STATE.HIDDEN };
  // The active pass object rides along so callers can show its entry counts.
  if (hasActive) return { state: PASS_STATE.ACTIVE, pass: activePass, endDate: activePass?.EndDate };
  if (inactivePasses.length) return { state: PASS_STATE.INACTIVE, endDate: inactivePasses[0].EndDate };
  return { state: PASS_STATE.NONE };
};

/**
 * Reads the pass state off the store, fetching once if nothing else has —
 * covers a direct URL hit or a hard refresh, where mainPage never ran.
 */
export default function usePassState() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSlice.mainUserData?.Token);
  const id = useSelector((state) => state.userSlice.mainUserData?.ID);
  const phone = useSelector((state) => state.userSlice.mainUserData?.Phone);
  const hasActive = useSelector(hasActiveMultipleEntryPassSelector);
  const activePass = useSelector(activeMultipleEntryPassesDetailsSelector);
  const inactivePasses = useSelector(inactiveMultipleEntryPassesSelector);
  const passesFetched = useSelector(selectPassesFetched);

  // Primitive deps: mainUserData is replaced wholesale by updateMainUserData,
  // so depending on the object itself would re-fire on every login pass.
  useEffect(() => {
    if (passesFetched) return;
    if (!token || !id || !phone) return;
    dispatch(fetchMultipleEntryPasses({ token, id, phone }));
  }, [token, id, phone, passesFetched, dispatch]);

  return derivePassState({ loaded: passesFetched, hasActive, activePass, inactivePasses });
}
